import gql from 'graphql-tag';

import { useQuery, type ApolloError } from '@apollo/react-hooks';
import { type DSRC_DECISION_TYPE } from '../../../../DRCAssessment/components/constants';
import { type ASSESSMENT_DETAIL_MODULE } from '../../../../components';

type GetValidateDsrcAssessment = {
  validateDsrcAssessment: ValidateDsrcAssessment;
  assessedPubIds: string[];
  assessedMeritIds: string[];
  initializedAqObjs: Array<{
    academicQualificationReferenceId: string;
    convertedGPA: number;
  }>;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

type ValidateDsrcAssessment = {
  dsrcAdmissionForm: {
    dsrcDecision?: DSRC_DECISION_TYPE;
    entranceRequirement?: string;
    entranceRequirementWaive?: string;
    confirmationChairInTeam?: string;
    stipendApproval?: boolean;
  };
  hasDoneArr: ASSESSMENT_DETAIL_MODULE[];
};

const GET_VALIDATE_DRC_ASSESSMENT = gql`
  query ($applicationId: String!) {
    assessmentDetail(applicationId: $applicationId) {
      dsrcAdmissionForm {
        dsrcDecision
        stipendApproval
        entranceRequirement
        entranceRequirementWaive
        confirmationChairInTeam
      }
      dsrcAssessment {
        publicationAssessments {
          publicationReferenceId
        }
        meritAssessments {
          meritReferenceId
        }
        academicAssessments {
          academicQualificationReferenceId
          convertedGPA
        }
      }
      dsrcAssessmentHasEdited
    }
  }
`;

export const useValidateDsrcAssessment = (
  applicationId: string,
): GetValidateDsrcAssessment => {
  const { data, loading, error, refetch } = useQuery(
    GET_VALIDATE_DRC_ASSESSMENT,
    {
      variables: { applicationId },
      fetchPolicy: 'network-only',
    },
  );

  const validateDsrcAssessmentData: ValidateDsrcAssessment =
    data?.assessmentDetail ?? {};

  const { dsrcAdmissionForm } = validateDsrcAssessmentData;
  const {
    dsrcDecision,
    entranceRequirement,
    entranceRequirementWaive,
    confirmationChairInTeam,
    stipendApproval,
  } = dsrcAdmissionForm ?? {};

  const validateDsrcAssessment: ValidateDsrcAssessment = {
    dsrcAdmissionForm: {
      dsrcDecision: (dsrcDecision as DSRC_DECISION_TYPE) ?? undefined,
      entranceRequirement,
      entranceRequirementWaive,
      confirmationChairInTeam,
      stipendApproval,
    },
    hasDoneArr: data?.assessmentDetail?.dsrcAssessmentHasEdited ?? [],
  };
  const assessedPubObjs = (data?.assessmentDetail?.dsrcAssessment
    ?.publicationAssessments ?? []) as Array<{
    publicationReferenceId: string;
  }>;
  const assessedMeritObjs = (data?.assessmentDetail?.dsrcAssessment
    ?.meritAssessments ?? []) as Array<{ meritReferenceId: string }>;

  const initializedAqObjs = (data?.assessmentDetail?.dsrcAssessment
    ?.academicAssessments ?? []) as Array<{
    academicQualificationReferenceId: string;
    convertedGPA: number;
  }>;

  const assessedPubIds: string[] =
    assessedPubObjs?.map(obj => obj.publicationReferenceId) ?? [];
  const assessedMeritIds: string[] =
    assessedMeritObjs?.map(obj => obj.meritReferenceId) ?? [];

  return {
    validateDsrcAssessment,
    assessedPubIds,
    assessedMeritIds,
    initializedAqObjs,
    loading,
    error,
    refetch,
  };
};
